// src/components/Modals/SubscriptionModal.jsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import PromoTokenInput from "../Billing/PromoTokenInput";
import { apiFetch } from "../../lib/apiFetch";
import { useAccess } from "../../contexts/AccessContext";
import { useEntitlements } from "../../contexts/EntitlementsContext";
import { useAuth } from "../../contexts/AuthContext";

// Helpers
function formatPlanName(plan) {
  const p = String(plan || "").toLowerCase();
  if (!p || p === "free") return "Free";
  if (p === "studio") return "Studio (monthly)";
  if (p === "studio_annual" || p === "studio-annual") return "Studio (yearly)";
  return String(plan);
}

function isSubscriptionActive(plan) {
  const p = String(plan || "").toLowerCase();
  return p && p !== "free";
}

function currentPlanMatches(currentPlan, target) {
  if (!currentPlan) return false;
  return String(currentPlan).toLowerCase() === String(target).toLowerCase();
}

function getStoredUserEmail() {
  if (typeof window === "undefined") return "";
  try {
    return localStorage.getItem("mudo:userEmail") || "";
  } catch {
    return "";
  }
}

export default function SubscriptionModal({ isOpen, onClose, onSelectPlan, currentPlan }) {
  const navigate = useNavigate();
  const [cycle, setCycle] = useState("monthly"); // 'monthly' | 'yearly'
  const [promoCode, setPromoCode] = useState("");
  const [emailForReceipt, setEmailForReceipt] = useState(() => getStoredUserEmail());
  const [currentUserEmail, setCurrentUserEmail] = useState(() => getStoredUserEmail());
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const containerRef = useRef(null);
  const fetchedInfoForKeyRef = useRef(null);
  const { refreshAccess, isUnlocked, daysRemaining, accessUntil } = useAccess() || {};
  const { trialEndsAtMs } = useEntitlements() || {};
  const { isAuthReady, user } = useAuth() || {};

  const closable = typeof onClose === "function";
  const hasUsedTrial = trialEndsAtMs != null && trialEndsAtMs > 0;

  // Check if user has local evidence of being paid
  const hasLocalPaidEvidence = isUnlocked || daysRemaining > 0 || (accessUntil && new Date(accessUntil) > new Date());

  useEffect(() => {
    if (!isOpen) {
      fetchedInfoForKeyRef.current = null;
      return;
    }

    const onKey = (e) => {
      if (e.key === "Escape" && closable) onClose?.();
    };

    window.addEventListener("keydown", onKey);
    setTimeout(() => containerRef.current?.querySelector("input")?.focus?.(), 60);

    const storedEmail = getStoredUserEmail();
    const authEmail = user?.email || "";
    const effectiveEmail = storedEmail || authEmail;
    setCurrentUserEmail(effectiveEmail);
    setEmailForReceipt((prev) => prev || effectiveEmail);

    // Fetch subscription info when modal opens
    const fetchSubscriptionInfo = async () => {
      // Guard: don't fetch until auth is ready; avoid noisy 401s during boot
      if (!isAuthReady) {
        setLoadingInfo(false);
        return;
      }
      if (!user?.id) {
        // Not logged in; existing code handles 401 too, but avoid unnecessary calls.
        setLoadingInfo(false);
        return;
      }

      // Dedupe: only fetch once per open+userId (StrictMode / re-renders / dep churn)
      const key = `${user.id}`;
      if (fetchedInfoForKeyRef.current === key) {
        return;
      }
      fetchedInfoForKeyRef.current = key;

      setLoadingInfo(true);
      try {
        const response = await apiFetch("/api/billing/subscription-info");
        if (response.ok) {
          const data = await response.json();
          setSubscriptionInfo(data);
          
          const stripeSubscriptionId = data?.subscription?.stripe_subscription_id;

          // Only attempt a sync when a Stripe subscription ID already exists
          if (stripeSubscriptionId) {
            try {
              const syncResponse = await apiFetch("/api/billing/sync-subscription");
              if (syncResponse.ok) {
                const syncData = await syncResponse.json();
                // Refetch subscription info after sync
                const refetchResponse = await apiFetch("/api/billing/subscription-info");
                if (refetchResponse.ok) {
                  const refetchData = await refetchResponse.json();
                  setSubscriptionInfo(refetchData);
                }
              } else {
                const syncError = await syncResponse.json();
              }
            } catch (syncError) {
            }
          }
        } else if (response.status === 401) {
          // Unauthorized - user not logged in, this is expected
        } else {
          // If we have local evidence of being paid, don't show an error
          if (hasLocalPaidEvidence) {
            setSubscriptionInfo({ ok: true, subscription: { status: 'active' }, localFallback: true });
          }
        }
      } catch (error) {
        // If we have local evidence of being paid, use that as fallback
        if (hasLocalPaidEvidence) {
          setSubscriptionInfo({ ok: true, subscription: { status: 'active' }, localFallback: true });
        }
      } finally {
        setLoadingInfo(false);
      }
    };

    fetchSubscriptionInfo();

    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose, closable, hasLocalPaidEvidence, isAuthReady, user?.id]);

  const money = (n) =>
    new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "AUD",
      maximumFractionDigits: 2,
    }).format(n);

  const handlePromoChange = (value) => {
    setPromoCode(value);
  };

  const handlePromoApplied = (result) => {
    // Promo successfully applied, close modal
    if (closable) {
      onClose?.();
    }
  };

  // Kicks off the server-side checkout flow for the selected plan.
  const startCheckout = async (planId) => {
    setLoadingPlan(planId);

    const trimmedEmail = emailForReceipt.trim();
    const trimmedPromo = promoCode.trim().toUpperCase();
    const fallbackEmail = (currentUserEmail || user?.email || "").trim();

    // Map planId to priceId (from env vars)
    const priceIdMap = {
      "studio-monthly": import.meta.env.VITE_STRIPE_PRICE_MONTHLY,
      "studio-yearly": import.meta.env.VITE_STRIPE_PRICE_ANNUAL,
    };

    const priceId = priceIdMap[planId];
    if (!priceId) {
      console.error("[SubscriptionModal] Price ID not configured for plan:", planId, { envVars: { monthly: import.meta.env.VITE_STRIPE_PRICE_MONTHLY, annual: import.meta.env.VITE_STRIPE_PRICE_ANNUAL } });
      alert("Pricing not configured. Please contact support.");
      setLoadingPlan(null);
      return;
    }

    const finalEmail = trimmedEmail || fallbackEmail;
    if (!finalEmail) {
      console.error("[SubscriptionModal] Email required for checkout");
      alert("Please enter your email to continue");
      setLoadingPlan(null);
      return;
    }

    const payload = {
      priceId,
      email: finalEmail,
      promo_code: trimmedPromo || "",
      marketing_opt_in: marketingOptIn,
    };

    console.log("[SubscriptionModal] Starting checkout:", { planId, priceId, email: finalEmail, hasPromo: !!trimmedPromo });

    try {
      const response = await apiFetch("/api/billing/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await response.json();

      console.log("[SubscriptionModal] Checkout response:", { ok: response.ok, hasUrl: !!data?.url });

      if (!response.ok) {
        console.error("[SubscriptionModal] checkout error:", data?.error);
        setLoadingPlan(null);
        return;
      }

      if (data?.url) {
        window.location.href = data.url;
        return;
      }

      console.error("[SubscriptionModal] checkout response missing URL");
      setLoadingPlan(null);
    } catch (err) {
      console.error("[SubscriptionModal] checkout exception:", err);
      setLoadingPlan(null);
    }
  };

  const isProcessing = Boolean(loadingPlan);

  const handleCancelSubscription = async () => {
    if (!confirm('Are you sure you want to cancel your subscription?\n\nYou will have access until the end of your billing period.')) {
      return;
    }

    setCancelling(true);
    try {
      const response = await apiFetch('/api/billing/cancel-subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await response.json();

      if (response.ok) {
        console.log('[SubscriptionModal] Subscription cancelled:', data);
        window.dispatchEvent?.(
          new CustomEvent('mudo:toast', {
            detail: { type: 'success', message: 'Subscription cancelled. You have access until ' + new Date(data.currentPeriodEnd).toLocaleDateString() },
          })
        );
        // Refresh subscription info
        const infoResponse = await apiFetch('/api/billing/subscription-info');
        if (infoResponse.ok) {
          const infoData = await infoResponse.json();
          setSubscriptionInfo(infoData);
        }
      } else {
        console.error('[SubscriptionModal] Cancel failed:', data);
        window.dispatchEvent?.(
          new CustomEvent('mudo:toast', {
            detail: { type: 'error', message: data.error || 'Failed to cancel subscription' },
          })
        );
      }
    } catch (error) {
      console.error('[SubscriptionModal] Cancel exception:', error);
      window.dispatchEvent?.(
        new CustomEvent('mudo:toast', {
          detail: { type: 'error', message: 'Failed to cancel subscription' },
        })
      );
    } finally {
      setCancelling(false);
    }
  };

  if (!isOpen) return null;

  const isActive =
    subscriptionInfo?.subscription?.status === "active" ||
    (accessUntil && new Date(accessUntil) > new Date());

  const sub = subscriptionInfo?.subscription || null;
  const promo = subscriptionInfo?.activePromo || null;

  // Debug: Log subscription details
  console.log("[SubscriptionModal] subscription details", {
    status: subscriptionInfo?.subscription?.status,
    cancelAtPeriodEnd: subscriptionInfo?.subscription?.cancelAtPeriodEnd,
    planName: sub?.planName,
    planInterval: sub?.planInterval,
    priceId: sub?.price_id,
    accessUntil,
    isActive,
    fullSub: sub,
  });

  // Derive plan name as fallback if API didn't provide it
  const displayPlanName = sub?.planName || 
    (currentPlan && currentPlan !== 'free' ? currentPlan : null) ||
    'Studio';
  const displayPlanInterval = sub?.planInterval || 'month';

  // Show "Manage Subscription" view if user already has active access
  if (isActive && !subscriptionInfo?.subscription?.cancelAtPeriodEnd) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sub-title"
        onMouseDown={(e) => e.target === e.currentTarget && closable && onClose?.()}
      >
        <div
          ref={containerRef}
          className="relative max-w-2xl w-full outline-none"
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* Liquid Glass Container */}
          <div className="relative bg-gradient-to-br from-zinc-900/90 via-zinc-900/95 to-black/90 backdrop-blur-xl rounded-3xl p-8 md:p-10 shadow-2xl border border-white/10">
            {/* Ambient Glow */}
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-green-500/5 via-transparent to-orange-500/5 pointer-events-none"></div>
            
            {/* Inner Highlight */}
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-b from-white/5 to-transparent pointer-events-none"></div>

            {/* Close Button */}
            {closable && (
              <button
                onClick={onClose}
                className="absolute top-6 right-6 text-gray-400 hover:text-white transition-colors text-2xl z-10"
                aria-label="Close"
                title="Esc"
              >
                ×
              </button>
            )}

            {/* Header with Success Icon */}
            <div className="relative text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-green-400 to-green-600 mb-4 animate-pulse shadow-lg shadow-green-500/50">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 id="sub-title" className="text-3xl md:text-4xl font-bold text-white mb-2">
                Subscription Active
              </h2>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/20 border border-green-500/30">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span className="text-sm font-semibold text-green-300 uppercase tracking-wide">Active</span>
              </div>
            </div>

            {/* Plan Card - Liquid Glass */}
            <div className="relative mb-6 rounded-2xl bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-md border border-white/10 p-6 shadow-xl overflow-hidden">
              {/* Accent Glow */}
              <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-orange-500 via-orange-400 to-orange-600"></div>
              
              <div className="relative space-y-4">
                <div>
                  <div className="text-xs font-semibold text-orange-300/80 uppercase tracking-wider mb-2">Current Plan</div>
                  <div className="text-2xl md:text-3xl font-bold text-white capitalize">
                    {displayPlanName?.replace(/_/g, ' ')}
                  </div>
                  <div className="text-sm text-white/60 mt-1 capitalize">
                    Billed {displayPlanInterval === 'year' ? 'annually' : 'monthly'}
                  </div>
                </div>

                {sub?.currentPeriodEnd && (
                  <div className="pt-4 border-t border-white/10">
                    <div className="text-xs font-semibold text-orange-300/80 uppercase tracking-wider mb-2">Next Renewal</div>
                    <div className="text-lg font-semibold text-white">
                      {new Date(sub.currentPeriodEnd).toLocaleDateString('en-AU', {
                        weekday: 'short',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </div>
                    <div className="text-xs text-white/50 mt-1">Renews automatically</div>
                  </div>
                )}
              </div>
            </div>

            {/* Active Promo Card */}
            {promo && (
              <div className="relative mb-6 rounded-2xl bg-gradient-to-br from-purple-500/10 to-purple-600/10 backdrop-blur-md border border-purple-500/30 p-5 overflow-hidden">
                <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-purple-500 to-purple-600"></div>
                
                <div className="relative flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <svg className="w-5 h-5 text-purple-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                      </svg>
                      <span className="text-xs font-semibold text-purple-300 uppercase tracking-wider">Active Promo</span>
                    </div>
                    <div className="text-xl font-mono font-bold text-white mb-1">
                      {promo.code}
                    </div>
                    {promo.description && (
                      <div className="text-sm text-white/60">
                        {promo.description}
                      </div>
                    )}
                    <div className="flex gap-4 mt-2 text-xs text-white/50">
                      {promo.accessDays && (
                        <span>+{promo.accessDays} days</span>
                      )}
                      {promo.discountPercent && (
                        <span>{promo.discountPercent}% off</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="relative space-y-3">
              <button
                onClick={() => {
                  // Always call onClose if provided
                  if (onClose) {
                    onClose();
                  }

                  // Then ALWAYS navigate based on user role
                  try {
                    const role = localStorage.getItem("mudo:userRole");
                    const intentFrom = localStorage.getItem("mudo:switchToStudioIntentFrom");
                    let target;

                    if (role === "studio" || !role) {
                      // Studio users or unset role -> go to studio dashboard
                      localStorage.setItem("mudo:userRole", "studio");
                      target = intentFrom || "/dashboard/home";
                    } else {
                      // Visitor users -> go to discover
                      localStorage.setItem("mudo:userRole", "visitor");
                      target = "/discover";
                    }

                    // Try react-router navigate first, fallback to window.location
                    if (navigate) {
                      navigate(target, { replace: true });
                    } else {
                      window.location.href = target;
                    }
                  } catch (err) {
                    console.error('[SubscriptionModal] Navigation error:', err);
                    // Safe fallback
                    window.location.href = "/dashboard/home";
                  }
                }}
                className="w-full px-6 py-4 rounded-xl bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold text-lg shadow-lg shadow-green-500/30 hover:shadow-green-500/50 transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
              >
                Continue Using Mudo
              </button>

              <button
                onClick={handleCancelSubscription}
                disabled={cancelling}
                className={`w-full px-6 py-3 rounded-xl border-2 border-red-500/30 text-red-300 font-semibold bg-red-500/5 backdrop-blur-sm transition-all duration-200 ${
                  cancelling
                    ? 'opacity-60 cursor-not-allowed'
                    : 'hover:bg-red-500/10 hover:border-red-500/50 transform hover:scale-[1.01] active:scale-[0.99]'
                }`}
              >
                {cancelling ? 'Cancelling...' : 'Cancel Subscription'}
              </button>

              {/* Footer Microtext */}
              <div className="pt-4 space-y-2 text-center">
                <div className="flex items-center justify-center gap-2 text-xs text-white/40">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <span>Billing secured by Stripe</span>
                </div>
                <p className="text-xs text-white/30 leading-relaxed max-w-md mx-auto">
                  Cancel anytime. Access until {sub?.currentPeriodEnd && 
                    new Date(sub.currentPeriodEnd).toLocaleDateString('en-AU', { month: 'short', day: 'numeric', year: 'numeric' })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show "Subscription Cancelled" view if subscription is marked for cancellation
  if (sub?.cancelAtPeriodEnd) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sub-title"
        onMouseDown={(e) => e.target === e.currentTarget && closable && onClose?.()}
      >
        <div
          ref={containerRef}
          className="bg-zinc-900 rounded-2xl p-6 md:p-8 max-w-2xl w-full shadow-2xl relative outline-none text-white"
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* Close */}
          {closable && (
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-white text-2xl"
              aria-label="Close"
              title="Esc"
            >
              &times;
            </button>
          )}

          {/* Header */}
          <h2 id="sub-title" className="text-3xl font-bold text-center text-orange-400 mb-8">
            ⏱️ Subscription Ending
          </h2>

          {/* Warning Card */}
          <div className="bg-gradient-to-r from-orange-500/10 to-orange-600/10 border border-orange-500/30 rounded-lg p-6 mb-6">
            <div className="space-y-4">
              <div>
                <div className="text-sm text-orange-300 font-semibold mb-1">Access Until</div>
                <div className="text-2xl font-bold text-white">
                  {sub?.currentPeriodEnd && 
                    new Date(sub.currentPeriodEnd).toLocaleDateString('en-AU', {
                      weekday: 'short',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                </div>
              </div>

              <div className="pt-4 border-t border-orange-500/20">
                <p className="text-sm text-orange-200">
                  Your subscription will end on the date above. To continue using Mudo, you'll need to subscribe again or apply a promo code.
                </p>
              </div>
            </div>
          </div>

          {/* Active Promo Card */}
          {promo && (
            <div className="bg-gradient-to-r from-purple-500/10 to-purple-600/10 border border-purple-500/30 rounded-lg p-6 mb-6">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <svg className="w-5 h-5 text-purple-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                  </svg>
                  <span className="text-sm font-semibold text-purple-300">Active Promo Code</span>
                </div>
                <div className="text-2xl font-mono font-bold text-white">
                  {promo.code}
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="space-y-3">
            <button
              onClick={() => setCycle("monthly")}
              className="w-full px-4 py-3 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-semibold transition-colors"
            >
              Renew Subscription
            </button>

            <button
              onClick={onClose}
              className="w-full px-4 py-3 rounded-lg border border-white/20 text-white font-semibold hover:bg-white/10 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="sub-title"
      onMouseDown={(e) => e.target === e.currentTarget && closable && onClose?.()}
    >
      <div
        ref={containerRef}
        className="bg-zinc-900 rounded-2xl p-6 md:p-8 max-w-5xl w-full shadow-2xl relative outline-none text-white"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Close (only when closable) */}
        {closable && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-white text-2xl"
            aria-label="Close"
            title="Esc"
          >
            &times;
          </button>
        )}

        {/* Header */}
        <h2 id="sub-title" className="text-3xl font-bold text-center text-orange-400 mb-6">
          Choose Your Mudo Plan
        </h2>

        {/* Billing cycle toggle */}
        <div className="flex items-center justify-center mb-5">
          <div className="inline-flex rounded-full border border-white/10 bg-white/5 p-1 text-sm">
            <button
              className={`px-3 py-1.5 rounded-full ${
                cycle === "monthly" ? "bg-orange-500 text-white" : "text-white/80 hover:text-white"
              }`}
              onClick={() => setCycle("monthly")}
            >
              Monthly <span className="ml-1 text-[10px] opacity-80">(7-day trial)</span>
            </button>
            <button
              className={`px-3 py-1.5 rounded-full ${
                cycle === "yearly" ? "bg-orange-500 text-white" : "text-white/80 hover:text-white"
              }`}
              onClick={() => setCycle("yearly")}
              title="~10% discount"
            >
              Yearly <span className="ml-1 text-[10px] opacity-80">(-10%)</span>
            </button>
          </div>
        </div>

        {/* Email Input */}
        <div className="mb-6">
          <input
            className="px-4 py-3 rounded-lg bg-black/30 border border-white/10 w-full text-white placeholder-white/40 focus:outline-none focus:border-orange-400/50 focus:ring-2 focus:ring-orange-400/20 transition-all"
            placeholder="Email for receipt (optional)"
            type="email"
            value={emailForReceipt}
            onChange={(e) => setEmailForReceipt(e.target.value)}
          />
        </div>

        {/* Promo Token Input */}
        <div className="mb-6">
          <PromoTokenInput
            value={promoCode}
            onChange={handlePromoChange}
            onApplied={handlePromoApplied}
            showInlineValidation={true}
          />
        </div>

        {/* Current Subscription & Promo Status */}
        {subscriptionInfo && (
          <div className="mb-6 space-y-3">
            {/* Active Subscription */}
            {subscriptionInfo.subscription && (
              <div className="bg-gradient-to-r from-orange-500/10 to-orange-600/10 border border-orange-500/30 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                      <span className="text-sm font-semibold text-orange-300">Active Subscription</span>
                    </div>
                    <div className="text-white font-bold text-lg">
                      {subscriptionInfo.subscription.planName?.replace(/_/g, ' ') || 'Studio Plan'}
                      {subscriptionInfo.subscription.planInterval && (
                        <span className="text-sm text-white/60 ml-2">
                          ({subscriptionInfo.subscription.planInterval})
                        </span>
                      )}
                    </div>
                    {subscriptionInfo.subscription.currentPeriodEnd && (
                      <div className="text-xs text-white/60 mt-1">
                        Renews: {new Date(subscriptionInfo.subscription.currentPeriodEnd).toLocaleDateString()}
                      </div>
                    )}
                    {subscriptionInfo.subscription.cancelAtPeriodEnd && (
                      <div className="text-xs text-red-300 mt-1">
                        ⚠️ Cancels at period end
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Active Promo Code */}
            {promo && (
              <div className="bg-gradient-to-r from-purple-500/10 to-purple-600/10 border border-purple-500/30 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <svg className="w-4 h-4 text-purple-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                      </svg>
                      <span className="text-sm font-semibold text-purple-300">Active Promo</span>
                    </div>
                    <div className="text-white font-mono font-bold text-base">
                      {promo.code}
                    </div>
                    {promo.description && (
                      <div className="text-xs text-white/70 mt-1">
                        {promo.description}
                      </div>
                    )}
                    <div className="flex gap-3 mt-2 text-xs text-white/60">
                      {promo.accessDays && (
                        <span>+{promo.accessDays} days access</span>
                      )}
                      {promo.discountPercent && (
                        <span>{promo.discountPercent}% discount</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Current plan banner */}
        <div className="mb-4 text-center">
          <div className="inline-block bg-white/5 border border-white/10 text-sm text-white/80 rounded px-3 py-2">
            Current plan: {formatPlanName(displayPlanName)}
            <span className="ml-2 text-xs text-white/60 capitalize">{displayPlanInterval === 'year' ? 'billed annually' : 'billed monthly'}</span>
            {isActive ? (
              <span className="ml-2 text-xs text-green-300">• active</span>
            ) : null}
          </div>
        </div>

        {/* Plans */}
        <div className="flex flex-col md:flex-row gap-6 justify-center">
          {/* Free */}
          <article className="bg-zinc-800 rounded-xl p-6 flex-1 text-center border border-white/10">
            <h3 className="text-2xl font-bold mb-1">Free Plan</h3>
            <p className="text-4xl font-bold text-green-400 mb-1">$0</p>
            <p className="text-xs text-white/60 mb-4">forever</p>
            <ul className="text-gray-300 text-left mb-6 space-y-2">
              <li>3 products in calculator</li>
              <li>Full access to all app features for the first 7 days</li>
            </ul>
            <button
              disabled={isProcessing || String(currentPlan || "").toLowerCase() === "free"}
              onClick={() => onSelectPlan?.("free")}
              className={`bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded w-full ${
                String(currentPlan || "").toLowerCase() === "free" ? "opacity-60 cursor-not-allowed" : ""
              }`}
            >
              {String(currentPlan || "").toLowerCase() === "free" ? "Current plan" : "Continue Free"}
            </button>
          </article>

          {/* Studio Monthly */}
          <article
            className={`bg-zinc-800 rounded-xl p-6 flex-1 text-center relative border-2 ${
              cycle === "monthly" ? "border-orange-500" : "border-white/10"
            }`}
          >
            {cycle === "monthly" && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 text-xs px-2 py-0.5 rounded-full bg-orange-500 text-black border border-orange-300 shadow">
                Recommended
              </div>
            )}
            <h3 className="text-2xl font-bold mb-1">Studio — Monthly</h3>
            <p className="text-4xl font-bold text-orange-400 mb-1">{money(12.99)}</p>
            <p className="text-xs text-white/60 mb-4">/ month • 7-day free trial</p>
            <ul className="text-gray-300 text-left mb-6 space-y-2">
              <li>Unlimited products & tasks</li>
              <li>Full calculator & planner</li>
              <li>Invoicing & reports</li>
              <li>Single user</li>
            </ul>
            <label className="flex items-start gap-3 text-sm text-white/80 mb-4">
              <input
                type="checkbox"
                checked={marketingOptIn}
                onChange={(e) => setMarketingOptIn(e.target.checked)}
                className="mt-1"
              />
              <span>
                Keep me updated about Mudo features, studio tools, and product updates.
              </span>
            </label>
            <button
              disabled={isProcessing || cycle !== "monthly" || currentPlanMatches(currentPlan, "studio")}
              onClick={() => startCheckout("studio-monthly")}
              className={`text-white py-2 px-4 rounded w-full ${
                isProcessing || cycle !== "monthly" || currentPlanMatches(currentPlan, "studio")
                  ? "bg-orange-400/60"
                  : "bg-orange-500 hover:bg-orange-600"
              }`}
              title={hasUsedTrial ? "Subscribe to Studio Monthly" : "Start 7-day free trial"}
            >
              {currentPlanMatches(currentPlan, "studio")
                ? "Current plan"
                : loadingPlan === "studio-monthly"
                ? "Starting..."
                : hasUsedTrial
                ? "Subscribe"
                : "Start 7-day free trial"}
            </button>
          </article>

          {/* Studio Yearly */}
          <article
            className={`bg-zinc-800 rounded-xl p-6 flex-1 text-center relative border-2 ${
              cycle === "yearly" ? "border-purple-500" : "border-white/10"
            }`}
          >
            <h3 className="text-2xl font-bold mb-1">Studio — Yearly</h3>
            <p className="text-4xl font-bold text-purple-400 mb-1">{money(140)}</p>
            <p className="text-xs text-white/60 mb-4">/ year (≈10% off)</p>
            <ul className="text-gray-300 text-left mb-6 space-y-2">
              <li>Everything in Monthly</li>
              <li>~10% discount</li>
              <li>Priority roadmap votes</li>
            </ul>
            <button
              disabled={isProcessing || cycle !== "yearly" || currentPlanMatches(currentPlan, "studio_annual")}
              onClick={() => startCheckout("studio-yearly")}
              className={`text-white py-2 px-4 rounded w-full ${
                isProcessing || cycle !== "yearly" || currentPlanMatches(currentPlan, "studio_annual")
                  ? "bg-purple-400/60"
                  : "bg-purple-500 hover:bg-purple-600"
              }`}
            >
              {currentPlanMatches(currentPlan, "studio_annual")
                ? "Current plan"
                : loadingPlan === "studio-yearly"
                ? "Processing..."
                : "Go Yearly"}
            </button>
          </article>
        </div>

        <p className="text-xs text-gray-500 text-center mt-6">
          By choosing a plan, you agree to our{" "}
          <a href="#" className="underline hover:text-gray-300">
            Terms &amp; Conditions
          </a>
          .
        </p>
      </div>
    </div>
  );
}
